import Button from '@jetbrains/ring-ui/components/button/button'
import Dialog from '@jetbrains/ring-ui/components/dialog/dialog'
import {ControlsHeight} from '@jetbrains/ring-ui/components/global/controls-height'
import {Content, Header} from '@jetbrains/ring-ui/components/island/island'
import Panel from '@jetbrains/ring-ui/components/panel/panel'
import Radio from '@jetbrains/ring-ui/components/radio/radio'
import {useCallback, useState} from 'react'

import {ConfirmationDecision, type VersionedSettingsDto} from '../../services/pipelinesApi.types'
import ConfigurationStorageSwitch from '../ConfigurationStorage/ConfigurationStorageSwitch'

import styles from './ConfigurationStorageDialog.css'

const HEADER_LABEL = 'YAML config file detected'

type Props = {
  visible?: boolean
  isLoading?: boolean
  isCreationMode?: boolean
  onConfirm: (settings: VersionedSettingsDto) => void
  onClose?: () => void
}

function ConfigurationStorageDialog({
  visible = false,
  isLoading,
  isCreationMode = false,
  onConfirm,
  onClose,
}: Props) {
  const [confirmationDecision, setConfirmationDecision] = useState(
    isCreationMode ? ConfirmationDecision.IMPORT : ConfirmationDecision.OVERRIDE,
  )
  const [storedInRepo, setStoredInRepo] = useState(true)

  const onImportOptionChange = useCallback((value: string) => {
    setConfirmationDecision(value as ConfirmationDecision)
  }, [])

  const onStorageChange = useCallback((value: VersionedSettingsDto) => {
    setStoredInRepo(value.storedInRepo ?? false)
  }, [])

  const handleConfirm = useCallback(() => {
    onConfirm({
      storedInRepo,
      confirmationDecision,
    })
  }, [onConfirm, storedInRepo, confirmationDecision])

  return (
    <Dialog
      showCloseButton={!isCreationMode}
      show={visible}
      label={HEADER_LABEL}
      onCloseAttempt={onClose}
    >
      <Header>{HEADER_LABEL}</Header>
      <Content scrollableWrapperClassName={styles.content}>
        {isCreationMode ? (
          <Radio value={confirmationDecision} onChange={onImportOptionChange}>
            <Radio.Item value={ConfirmationDecision.IMPORT} className={styles.option}>
              {'Import pipeline from .teamcity.yml'}
            </Radio.Item>
            {confirmationDecision === ConfirmationDecision.IMPORT && (
              <ConfigurationStorageSwitch
                storedInRepo={storedInRepo}
                changeStorageHandler={onStorageChange}
              />
            )}
            <Radio.Item className={styles.option} value={ConfirmationDecision.OVERRIDE}>
              {'Create a new pipeline'}
            </Radio.Item>
            {confirmationDecision === ConfirmationDecision.OVERRIDE && (
              <>
                {storedInRepo && (
                  <div>
                    {
                      'If you choose to create a new file, remember that the previous YAML file will be deleted and a new one created.'
                    }
                  </div>
                )}
                <ConfigurationStorageSwitch
                  className={styles.switch}
                  storedInRepo={storedInRepo}
                  changeStorageHandler={onStorageChange}
                />
              </>
            )}
          </Radio>
        ) : (
          <>
            <div className={styles.info}>
              {'The remote repository already contains the .teamcity.yml file. ' +
                'You can replace it with current pipeline settings or apply settings from the file to your current pipeline.'}
            </div>
            <Radio value={confirmationDecision} onChange={onImportOptionChange}>
              <Radio.Item value={ConfirmationDecision.OVERRIDE} className={styles.option}>
                {'Rewrite the .teamcity.yml file'}
              </Radio.Item>
              <Radio.Item value={ConfirmationDecision.IMPORT}>
                {'Import pipeline from .teamcity.yml'}
              </Radio.Item>
            </Radio>
          </>
        )}
      </Content>
      <Panel>
        <Button primary loader={isLoading} height={ControlsHeight.M} onClick={handleConfirm}>
          {'Confirm'}
        </Button>
      </Panel>
    </Dialog>
  )
}

export default ConfigurationStorageDialog
